import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import config from "../Config/config";
import {
  checkDocsPermission,
  stripHtml,
} from "./infoShared";
import DocContentEditor from "./DocContentEditor";

const InfoSectionEdit = () => {
  const { docId, sectionId } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [order, setOrder] = useState(0);
  const [parentId, setParentId] = useState("");
  const [sections, setSections] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);

  const [permissionLoading, setPermissionLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  useEffect(() => {
    const run = async () => {
      const permission = await checkDocsPermission();
      setHasPermission(permission.allowed);
      setPermissionError(permission.error);
      setPermissionLoading(false);
    };

    run();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const [sectionResponse, listResponse] = await Promise.all([
          axios.get(`${config.API_BASE_URL}/docs/${docId}/sections/${sectionId}`),
          axios.get(`${config.API_BASE_URL}/docs/${docId}/sections`),
        ]);

        const section = sectionResponse.data || {};
        setTitle(section.title || "");
        setContent(section.content || "");
        setOrder(Number(section.order) || 0);
        setParentId(section.parentId || "");
        setSections((listResponse.data || []).filter((item) => item.id !== sectionId));
      } catch (err) {
        setLoadError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [docId, sectionId]);

  const summary = useMemo(() => {
    const plain = stripHtml(content);
    const words = plain ? plain.split(" ").filter(Boolean).length : 0;
    return {
      words,
      chars: plain.length,
    };
  }, [content]);

  const canSubmit = title.trim().length > 0 && !saving;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      await axios.put(`${config.API_BASE_URL}/docs/${docId}/sections/${sectionId}`, {
        title: title.trim(),
        content,
        order: Number(order) || 0,
        parentId: parentId || null,
      });

      navigate(`/docs/${docId}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (permissionLoading || loading) {
    return (
      <div className="flex justify-center mt-10">
        <div className="px-4 py-3 rounded-xl border border-slate-200 bg-white/90 text-slate-700 text-sm shadow-sm">
          Loading...
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex justify-center mt-10 px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          {permissionError || "Access denied."}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex justify-center mt-10 px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          Error: {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <section className="lg:col-span-2 rounded-2xl border border-slate-200/90 bg-white/95 shadow-[0_18px_40px_rgba(15,23,42,0.10)] p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Edit Section
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Adjust this section's placement and improve the content flow.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/docs/${docId}`)}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Back to Document
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-700">
                Section Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-700">
                  Order
                </label>
                <input
                  type="number"
                  value={order}
                  onChange={(event) => setOrder(Number(event.target.value) || 0)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-700">
                  Parent Section
                </label>
                <select
                  value={parentId}
                  onChange={(event) => setParentId(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-500"
                >
                  <option value="">None (Top Level)</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.title || `Section ${section.order || 0}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <DocContentEditor
              label="Content"
              value={content}
              onChange={setContent}
            />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
              <p className="text-xs text-slate-500">
                {summary.words} words • {summary.chars} characters
              </p>
              <button
                type="submit"
                disabled={!canSubmit}
                className={`inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all ${
                  canSubmit
                    ? "bg-slate-900 hover:bg-slate-800"
                    : "bg-slate-400 cursor-not-allowed"
                }`}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </section>

        <aside className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-sky-50 via-white to-slate-100 shadow-sm p-4 sm:p-5">
          <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
            Best Practices
          </h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>Keep sibling sections scoped to one specific outcome.</li>
            <li>Use the parent selector to avoid duplicated instructions.</li>
            <li>Short section titles help with left-nav readability.</li>
            <li>Inline screenshots should sit close to the related text.</li>
          </ul>
        </aside>
      </div>
    </div>
  );
};

export default InfoSectionEdit;
